import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useUsersStore } from '@/modules/settings/stores/users';
import MODALS from '@/modules/shared/utils/modals_enum';
import type { IUsers, IcreateUsers, IdeleteUsers } from '@/modules/settings/interfaces/Users';
import useUserInfo from '@/modules/shared/composable/useUserInfo';

interface IRolesMapping {
    'admin': string;
    'manager': string;
    'viewer': string;
}

const useUsers = () => {
    const storeUsers = useUsersStore();
    const { users } = storeToRefs(storeUsers);
    const { userInfo } = useUserInfo();

    const getUsers = async (): Promise<IUsers[]> => {
        if (users.value.length === 0) {
            storeUsers.setUsers([
                {
                    id: 'f29a5e5b-4aee-40d6-91a6-eaf446c65a61',
                    email: 'random.user16@example.org',
                    name: ' ',
                    role: 'manager',
                    tracking: 'false',
                    dataFrom: {
                        mailboxes: [],
                        teams: ['Accounting', 'Customer Service']
                    }
                },
                {
                    id: userInfo.value.id,
                    dataFrom: {
                        mailboxes: [],
                        teams: []
                    },
                    email: userInfo.value.email,
                    role: 'Admin',
                    tracking: 'false',
                    name: userInfo.value.name
                },
                {
                    id: 'f29a5e5b-4aee-40d6-91a6-eaf446c65a63',
                    email: 'toby.flenderson@dundermifflin.com',
                    name: 'Toby Flenderson',
                    role: 'Admin',
                    dataFrom: {
                        mailboxes: [],
                        teams: []
                    },
                    tracking: 'true'
                },
                {
                    id: 'a96c373a-82a7-4a5e-a881-405fe956dde8',
                    email: 'jim.halpert@dundermifflin.com',
                    name: 'Jim Halpert',
                    role: 'Manager',
                    dataFrom: {
                        mailboxes: [],
                        teams: ['Accounting', 'Customer Service']
                    },
                    tracking: 'true'
                },
                {
                    id: 'a96c373a-82a7-4a5e-a881-405fe956dde9',
                    email: 'dwight.schrute@dundermifflin.com',
                    name: 'Dwight Schrute',
                    role: 'Viewer',
                    dataFrom: {
                        mailboxes: [],
                        teams: []
                    },
                    tracking: 'true'
                }
            ]);
        }
        return await new Promise<IUsers[]>((resolve) => {
            resolve(users.value);
        });
    };

    const createUsers = async (usersData: IcreateUsers): Promise<void> => {
        deleteDemoUsers({ data: usersData });

        const ROLES_MAPPING: IRolesMapping = {
            admin: 'Admin',
            manager: 'Manager',
            viewer: 'Viewer'
        };

        const usersArray = usersData.emails.map((e: string) => {
            const fullName = /^([^]+)@(\w+).(\w+)$/.exec(e) || [''];
            return {
                id: Math.random().toString(),
                dataFrom: {
                    mailboxes: usersData.manage_mailboxes,
                    teams: usersData.manage_teams
                },
                email: e,
                role: ROLES_MAPPING[usersData.role as keyof IRolesMapping],
                tracking: 'false',
                name: fullName[1]
            };
        });

        storeUsers.setUsers([...users.value, ...usersArray]);
    };

    const deleteDemoUsers = async (usersData: IdeleteUsers) => {
        usersData.data.emails.forEach((email) => {
            const indexForDelete = users.value.findIndex((user) => user.email === email);
            if (indexForDelete !== -1) {
                const arrayUsers = [...users.value];
                arrayUsers.splice(indexForDelete, 1);
                storeUsers.setUsers([...arrayUsers]);
            }
        });
    };

    const deleteUsers = async (usersData: IdeleteUsers): Promise<void> => deleteDemoUsers(usersData);

    return {
        users,
        isErrorGet: false,

        // Method
        getUsers,
        createUsers,
        deleteUsers,
        removeData: (users: string[]) => ({
            name: MODALS.removeModal,
            data: {
                type: 'users',
                title: `Are you sure you want to remove ${users.length > 1 ? 'these users' : 'this user'}?`,
                text: `${users.join(', ')} will lose access to see email statistics.`,
                data: users
            }
        }),

        // computed
        onlyExistEmails: computed(() => [...users.value.map((m: { email: string }) => m.email)])
    };
};

export default useUsers;
